import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { MonoText } from "@/components/ui/CopyButton";
import { Badge } from "@/components/ui/Badge";
import { formatIDR, formatDuration } from "@/lib/format";
import { useLanding } from "@/hooks/useLanding";
import { strikethroughPrice, isAffiliate, deliveryLabel } from "@ag5r-store/shared";
import type { ProductListItem } from "@ag5r-store/shared";

/** Kartu angka di hero. */
function Stat({ value, label, suffix }: { value: string; label: string; suffix?: string }) {
  return (
    <div className="border border-border bg-surface rounded-sm px-3 py-3">
      <dd className="font-display text-3xl leading-none text-accent">
        {value}
        {suffix && <span className="ml-0.5 text-xl">{suffix}</span>}
      </dd>
      <dt className="mt-1.5 font-sans text-xs text-ink-muted">{label}</dt>
    </div>
  );
}

/** Harga terendah antar varian; 0 kalau produk belum punya varian. */
function minVariantPrice(p: ProductListItem): number {
  return p.variants.length > 0 ? Math.min(...p.variants.map((v) => v.price)) : 0;
}

export function LandingPage() {
  const [featured, setFeatured] = useState<ProductListItem[]>([]);
  const { data } = useLanding();

  // Produk affiliate tidak pernah dicoret: harga akhirnya ditentukan mitra,
  // jadi potongan yang kita klaim tidak akan pernah bisa dibuktikan.
  const featuredStrike = (p: ProductListItem) =>
    isAffiliate(p.delivery_type)
      ? null
      : strikethroughPrice(minVariantPrice(p), data?.discount_percent ?? 0);

  useEffect(() => {
    api.listProducts({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  // Fallback dipakai kalau /api/settings gagal — halaman tetap masuk akal.
  const eyebrow = data?.hero_eyebrow ?? "";
  const title = data?.hero_title || "Beli sekarang, aktif detik ini juga.";
  const subtitle =
    data?.hero_subtitle ||
    "Serial key, akun premium, dan file digital langsung masuk ke akun Anda begitu pembayaran selesai.";

  return (
    <div className="bg-void">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Dekorasi latar: di BELAKANG konten, opacity rendah, dan tidak pernah
            berada di bawah blok teks — keterbacaan tetap utuh. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-24 h-[26rem] w-[26rem] rounded-full
                     bg-accent/10 blur-3xl"
        />
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Kolom teks */}
            <div className="animate-rise">
              {eyebrow && (
                <span className="inline-block font-tech text-xs font-semibold uppercase tracking-[0.2em]
                                 text-accent border border-accent/40 bg-accent/10 px-3 py-1 rounded-sm">
                  {eyebrow}
                </span>
              )}

              <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold
                             tracking-tight text-ink-primary leading-[1.06]">
                {title}
              </h1>

              <p className="mt-5 font-sans text-base md:text-lg text-ink-muted max-w-xl leading-relaxed">
                {subtitle}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to={data?.cta_primary_href || "/products"}>
                  <Button variant="primary">
                    {data?.cta_primary_label || "Lihat Katalog"} →
                  </Button>
                </Link>
                {data?.cta_secondary_label && (
                  <a href={data.cta_secondary_href || "#cara-kerja"}>
                    <Button variant="secondary">{data.cta_secondary_label}</Button>
                  </a>
                )}
              </div>

              {/* Angka penjualan — produk dihitung langsung dari katalog. */}
              <dl className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
                <Stat value={String(data?.stat_products ?? "—")} label="Produk" />
                <Stat value={data?.stat_clients || "—"} label="Pembeli" />
                <Stat value={data?.stat_rating || "—"} label="Rating" suffix="★" />
              </dl>
            </div>

            {/* Kolom gambar — hanya dirender kalau owner mengisinya. */}
            {data?.hero_image_url ? (
              // Rasio 1:1. object-cover memotong sisi terpanjang, jadi gambar
              // apa pun tetap tampil persegi tanpa gepeng.
              <div className="border border-border-strong bg-surface rounded-lg overflow-hidden shadow-lift">
                <img
                  src={data.hero_image_url}
                  alt=""
                  className="w-full aspect-square object-cover"
                />
              </div>
            ) : (
              <div className="hidden lg:flex items-center justify-center border border-border
                              bg-surface rounded-lg aspect-square">
                <p className="font-sans text-sm text-ink-dim text-center px-6">
                  Tambahkan gambar hero lewat
                  <br />
                  <span className="text-ink-muted">Settings → Hero Image</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="section-heading">FEATURED_ITEMS</h2>
              <h3 className="mt-3 font-sans text-2xl text-ink-primary">
                Currently in rotation
              </h3>
            </div>
            <Link to="/products">
              <Button variant="ghost">View All →</Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {featured.slice(0, 3).map((p) => (
              <Link
                to={`/products?slug=${encodeURIComponent(p.slug)}`}
                key={p.id}
                className="block group"
              >
                <Panel padded={false} className="hover:border-terminal">
                  <div className="aspect-square bg-panel border-b border-border overflow-hidden">
                    {p.images[0] ? (
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      // Kode produk lebih berguna daripada teks "no_media",
                      // dan tetap terbaca sebagai kartu, bukan kotak kosong.
                      <div className="w-full h-full flex items-center justify-center bg-accent/10">
                        <span className="font-mono text-xl font-bold text-accent">
                          {p.product_code.slice(0, 4)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MonoText size="xs" color="ink-dim">{p.product_code}</MonoText>
                      {/* Produk affiliate ditandai terpisah — pembeli harus
                          tahu sejak landing bahwa tombolnya keluar dari toko
                          ini, bukan masuk ke keranjang. */}
                      <Badge variant={isAffiliate(p.delivery_type) ? "pending" : "active"}>
                        {deliveryLabel(p.delivery_type)}
                      </Badge>
                    </div>
                    <div className="font-sans text-base text-ink-primary line-clamp-2">
                      {p.name}
                    </div>
                    <div className="mt-3 flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        {isAffiliate(p.delivery_type) && p.variants.length === 0 ? (
                          // Tanpa varian tidak ada angka yang jujur untuk
                          // ditampilkan — "Rp 0" akan terbaca sebagai gratis.
                          <p className="font-mono text-sm text-accent truncate">Cek di mitra</p>
                        ) : (
                          <>
                            <p className="font-sans text-2xs text-ink-dim">mulai</p>
                            {/* Harga coret hanya tampilan; tagihan tetap harga varian. */}
                            {featuredStrike(p) && (
                              <p className="font-sans text-2xs text-ink-dim line-through leading-none">
                                {formatIDR(featuredStrike(p)!)}
                              </p>
                            )}
                            <p className="font-mono text-sm text-accent truncate">
                              {formatIDR(minVariantPrice(p))}
                            </p>
                          </>
                        )}
                      </div>
                      <p className="font-sans text-2xs text-ink-muted shrink-0">
                        {isAffiliate(p.delivery_type)
                          ? "via mitra ↗"
                          : `${p.variants.length} paket`}
                      </p>
                    </div>
                  </div>
                </Panel>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section id="cara-kerja" className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-ink-primary">
          Empat langkah, selesai dalam hitungan menit
        </h2>
        <p className="mt-3 font-sans text-base text-ink-muted max-w-2xl">
          Tidak ada antre, tidak ada chat admin, tidak ada email yang nyangkut di spam.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { n: "1", t: "Pilih produk", d: "Telusuri katalog, saring sesuai kebutuhan dan budget Anda." },
            { n: "2", t: "Pilih paket", d: "Tentukan durasi atau paket. Harga tampil jelas, tanpa biaya tersembunyi." },
            { n: "3", t: "Bayar", d: "QRIS, e-wallet, atau transfer bank. Konfirmasi otomatis, tanpa kirim bukti." },
            { n: "4", t: "Langsung pakai", d: "Kode atau akun muncul di halaman Akun Saya saat itu juga." },
          ].map((step) => (
            <div key={step.n} className="border border-border bg-surface rounded-lg p-5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm
                               bg-accent text-void font-heading text-lg font-extrabold">
                {step.n}
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-ink-primary">{step.t}</h3>
              <p className="mt-2 font-sans text-sm text-ink-muted leading-relaxed">{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* APA YANG ANDA DAPAT */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-ink-primary">
          Apa yang Anda dapat
        </h2>
        <p className="mt-3 font-sans text-base text-ink-muted max-w-2xl">
          Tiga jenis produk, semuanya tersimpan aman di akun Anda dan bisa dibuka kapan saja.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              t: "Serial key",
              d: "Kode lisensi resmi untuk aktivasi software. Dibuat khusus untuk Anda dan langsung terbit begitu pembayaran terkonfirmasi.",
              e: "AG5R-9F2K-18ZPQ-WIN11",
            },
            {
              t: "Akun premium",
              d: "Akun siap pakai lengkap dengan email dan password. Satu akun hanya untuk satu pembeli — tidak pernah dijual dua kali.",
              e: "premium@contoh.com : ••••••••",
            },
            {
              t: "File & link",
              d: "Tautan unduh langsung ke file yang Anda beli. Bisa diakses berulang kali dari akun Anda, tidak hangus.",
              e: "https://drive.google.com/…",
            },
          ].map((c) => (
            <div key={c.t} className="border border-border bg-surface rounded-lg p-6">
              <h3 className="font-heading text-xl font-bold text-ink-primary">{c.t}</h3>
              <p className="mt-3 font-sans text-sm text-ink-muted leading-relaxed">{c.d}</p>
              <div className="mt-4 border border-border bg-panel rounded-sm px-3 py-2">
                <code className="font-mono text-xs text-accent break-all">{c.e}</code>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA PENUTUP */}
      <section className="max-w-7xl mx-auto px-4 pb-8">
        <div className="border border-border-strong bg-surface rounded-xl px-6 py-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-ink-primary">
            Siap mulai?
          </h2>
          <p className="mt-3 font-sans text-base text-ink-muted max-w-xl mx-auto">
            Pilih produk, bayar, dan langsung pakai. Sesederhana itu.
          </p>
          <Link to="/products" className="inline-block mt-7">
            <Button variant="primary">Lihat Katalog →</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}